import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plus, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const STATUSES = ["ALL", "NEW", "SCHEDULED", "EN_ROUTE", "ON_SITE", "COMPLETE", "INVOICED", "CLOSED"];
const fmtDate = (iso) =>
  new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function Field({ label, required, error, children, testId }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6A6E85]" data-testid={testId ? `${testId}-label` : undefined}>
        {label} {required && <span className="text-[#DC2626]">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {error && <p className="text-xs text-[#DC2626] mt-1" data-testid={testId ? `${testId}-error` : undefined}>{error}</p>}
    </div>
  );
}

function NewJobModal({ onClose, onCreated }) {
  const [clients, setClients] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [techs, setTechs] = useState([]);
  const initDate = new Date();
  initDate.setHours(initDate.getHours() + 1, 0, 0, 0);
  const [date, setDate] = useState(initDate);
  const [time, setTime] = useState(
    `${String(initDate.getHours()).padStart(2, "0")}:${String(initDate.getMinutes()).padStart(2, "0")}`
  );
  const [form, setForm] = useState({
    client_id: "", site_id: "", asset_id: "", title: "",
    priority: "normal", assigned_tech_id: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => { setClients(r.data); setClientsLoaded(true); });
    api.get("/techs").then((r) => setTechs(r.data));
  }, []);

  useEffect(() => {
    if (!form.client_id) { setSites([]); return; }
    api.get("/sites", { params: { client_id: form.client_id } }).then((r) => {
      setSites(r.data);
      if (r.data.length === 1) setForm((f) => ({ ...f, site_id: r.data[0].id }));
    });
  }, [form.client_id]);

  useEffect(() => {
    if (!form.site_id) { setAssets([]); return; }
    api.get("/assets", { params: { site_id: form.site_id } }).then((r) => setAssets(r.data));
  }, [form.site_id]);

  const validate = () => {
    const e = {};
    if (!form.client_id) e.client_id = "Choose a client.";
    if (!form.site_id) e.site_id = "Choose a site.";
    if (!form.title.trim()) e.title = "Give the job a plain-English title.";
    if (!date) e.date = "Pick a date.";
    if (!time) e.time = "Pick a time.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const [h, m] = time.split(":").map(Number);
      const sched = new Date(date);
      sched.setHours(h || 0, m || 0, 0, 0);
      const payload = {
        ...form,
        assigned_tech_id: form.assigned_tech_id || null,
        asset_id: form.asset_id || null,
        scheduled_at: sched.toISOString(),
      };
      const r = await api.post("/jobs", payload);
      toast.success("Job created");
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create job");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-[#101038]/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-[#181854]">New job</h2>
        <p className="text-sm text-[#6A6E85] mt-1">Create a job and (optionally) assign it to a tech.</p>
        <form onSubmit={submit} className="mt-5 space-y-3" noValidate>
          <Field label="Client" required error={errors.client_id} testId="new-job-client">
            <select
              data-testid="new-job-client"
              value={form.client_id}
              onChange={(e) => { setForm({ ...form, client_id: e.target.value, site_id: "", asset_id: "" }); setErrors((x) => ({ ...x, client_id: null })); }}
              className="w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white"
            >
              <option value="">{clientsLoaded ? "Choose client…" : "Loading clients…"}</option>
              {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </Field>

          <Field label="Site" required error={errors.site_id} testId="new-job-site">
            <select
              data-testid="new-job-site"
              value={form.site_id}
              onChange={(e) => { setForm({ ...form, site_id: e.target.value, asset_id: "" }); setErrors((x) => ({ ...x, site_id: null })); }}
              disabled={!form.client_id}
              className="w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white disabled:bg-[#F5F6FA]"
            >
              <option value="">Choose site…</option>
              {sites.map((s) => (<option key={s.id} value={s.id}>{s.address}</option>))}
            </select>
          </Field>

          <Field label="Asset (optional)">
            <select
              data-testid="new-job-asset"
              value={form.asset_id}
              onChange={(e) => setForm({ ...form, asset_id: e.target.value })}
              disabled={!form.site_id}
              className="w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white disabled:bg-[#F5F6FA]"
            >
              <option value="">No specific asset</option>
              {assets.map((a) => (<option key={a.id} value={a.id}>{a.label} ({a.type})</option>))}
            </select>
          </Field>

          <Field label="Job title" required error={errors.title} testId="new-job-title">
            <input
              data-testid="new-job-title"
              placeholder="e.g. Cold room alarm — high temp"
              value={form.title}
              onChange={(e) => { setForm({ ...form, title: e.target.value }); setErrors((x) => ({ ...x, title: null })); }}
              className="w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority">
              <select
                data-testid="new-job-priority"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </Field>
            <Field label="Assign to">
              <select
                data-testid="new-job-tech"
                value={form.assigned_tech_id}
                onChange={(e) => setForm({ ...form, assigned_tech_id: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white"
              >
                <option value="">Leave unassigned</option>
                {techs.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Date" required error={errors.date}>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="new-job-date"
                    className="w-full inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white text-sm"
                  >
                    <span className="text-[#14162E]">{date ? format(date, "d MMM yyyy") : "Pick a date"}</span>
                    <CalendarIcon size={14} className="text-[#6A6E85]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { if (d) { setDate(d); setErrors((x) => ({ ...x, date: null })); } }}
                  />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Time" required error={errors.time}>
              <input
                type="time"
                data-testid="new-job-time"
                value={time}
                onChange={(e) => { setTime(e.target.value); setErrors((x) => ({ ...x, time: null })); }}
                className="w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white"
              />
            </Field>
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg bg-[#ECEDF6] hover:bg-[#E4E6EF] text-[#181854] font-medium transition-colors duration-200">Cancel</button>
            <button type="submit" disabled={busy} data-testid="new-job-submit" className="flex-1 px-4 py-2.5 rounded-lg bg-[#181854] hover:bg-[#101038] text-white font-medium transition-colors duration-200 disabled:opacity-60">{busy ? "Creating…" : "Create job"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function JobsList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [techFilter, setTechFilter] = useState("ALL");
  const [showNew, setShowNew] = useState(params.get("new") === "1");

  const load = () => api.get("/jobs").then((r) => setJobs(r.data));
  useEffect(() => { load(); api.get("/techs").then((r) => setTechs(r.data)); }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return jobs.filter((j) => {
      if (statusFilter !== "ALL" && j.status !== statusFilter) return false;
      if (techFilter !== "ALL") {
        if (techFilter === "UNASSIGNED" && j.assigned_tech_id) return false;
        if (techFilter !== "UNASSIGNED" && j.assigned_tech_id !== techFilter) return false;
      }
      if (query) {
        const blob = `${j.title} ${j.client_name} ${j.site_address} ${j.tech_name || ""}`.toLowerCase();
        if (!blob.includes(query)) return false;
      }
      return true;
    });
  }, [jobs, q, statusFilter, techFilter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#181854] tracking-tight">Jobs</h1>
          <p className="text-[#6A6E85] mt-1">{filtered.length} of {jobs.length}</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="jobs-new-btn" className="inline-flex items-center gap-2 bg-[#181854] hover:bg-[#101038] text-white px-4 py-2.5 rounded-lg font-medium transition-colors duration-200"><Plus size={16} /> New job</button>
      </div>

      <div className="bg-white rounded-xl border border-[#E4E6EF] p-4 shadow-sm">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6A6E85]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, client, site…" data-testid="jobs-search" className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="jobs-filter-status" className="px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white">
            {STATUSES.map((s) => (<option key={s} value={s}>{s === "ALL" ? "All statuses" : s}</option>))}
          </select>
          <select value={techFilter} onChange={(e) => setTechFilter(e.target.value)} data-testid="jobs-filter-tech" className="px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white">
            <option value="ALL">All techs</option>
            <option value="UNASSIGNED">Unassigned</option>
            {techs.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E4E6EF] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F6FA] border-b border-[#E4E6EF]">
              <tr className="text-left text-xs uppercase tracking-wider text-[#6A6E85]">
                <th className="px-4 py-3 font-semibold">Job</th>
                <th className="px-4 py-3 font-semibold">Client / Site</th>
                <th className="px-4 py-3 font-semibold">Tech</th>
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E6EF]">
              {filtered.map((j) => (
                <tr key={j.id} onClick={() => navigate(`/office/jobs/${j.id}`)} data-testid={`jobs-row-${j.id}`} className={`hover:bg-[#F5F6FA] cursor-pointer transition-colors duration-200 status-border-${j.status}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#14162E]">{j.title}</div>
                    <div className="text-xs text-[#6A6E85]">{j.asset_label || "No asset linked"}</div>
                  </td>
                  <td className="px-4 py-3 text-[#14162E]">
                    <div>{j.client_name}</div>
                    <div className="text-xs text-[#6A6E85]">{j.site_address}</div>
                  </td>
                  <td className="px-4 py-3 text-[#14162E]">{j.tech_name || <span className="text-[#6A6E85]">Unassigned</span>}</td>
                  <td className="px-4 py-3 text-[#14162E]">{fmtDate(j.scheduled_at)}</td>
                  <td className="px-4 py-3"><StatusPill status={j.status} /></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-[#6A6E85]">No jobs match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && (
        <NewJobModal
          onClose={() => { setShowNew(false); setParams({}); }}
          onCreated={(job) => { setShowNew(false); setParams({}); load(); navigate(`/office/jobs/${job.id}`); }}
        />
      )}
    </div>
  );
}
